<template>
  <div 
    v-if="!isArchived"
    :id="'card-' + id"
    class="history-note-card"
    :class="{ 'expanded': isExpanded, 'is-right-side': isRightSide }"
    @click="handleCardClick"
  >
    <!-- Comments badge - positioned relative to card -->
    <div v-if="commentsCount > 0" class="comments-badge">{{ commentsCount }}</div>
    
    <!-- Compact view (hidden when expanded) -->
    <div class="compact-content" v-if="!isExpanded">
      <span class="time-text">{{ time }}</span>
      <span class="author-text">{{ author }}</span>
    </div>
    
    <!-- Expanded content (shown when expanded) -->
    <transition name="note-expand">
      <div
        class="expanded-content"
        v-if="isExpanded"
      >
        <div class="expanded-header">
          <div class="header-left">
            <span class="time-text">{{ time }}</span>
            <span class="author-text">{{ author }}</span>
          </div>
          <div class="header-right">
            <button class="icon-btn edit-btn" @click.stop="startEdit">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M21.4555 5.41575C21.6477 5.70687 21.6157 6.10248 21.3594 6.35876L12.167 15.5511C12.0728 15.6454 11.9552 15.7128 11.8263 15.7465L7.99783 16.7465C7.87296 16.7791 7.74425 16.7784 7.62332 16.7476C7.49475 16.7149 7.37498 16.6482 7.27797 16.5511C7.08969 16.3629 7.01535 16.0889 7.08264 15.8313L8.08264 12.0028C8.11211 11.89 8.1674 11.7786 8.24389 11.6912L17.4703 2.46967C17.5511 2.38891 17.6484 2.32846 17.7543 2.29163C17.8328 2.26432 17.916 2.25 18.0007 2.25C18.1996 2.25 18.3903 2.32902 18.531 2.46967L21.3594 5.2981C21.396 5.33471 21.4281 5.37416 21.4555 5.41575ZM19.7684 5.82843L18.0007 4.06066L9.48251 12.5788L8.85752 14.9716L11.2503 14.3466L19.7684 5.82843Z" fill="#374957"/>
                <path d="M19.642 17.1603C19.9154 14.8227 20.0024 12.4688 19.903 10.1208C19.8982 10.0084 19.9405 9.89898 20.0201 9.81942L21.0034 8.83609C21.1243 8.71519 21.3309 8.79194 21.3422 8.96254C21.5271 11.7522 21.457 14.5545 21.1319 17.3346C20.8953 19.3571 19.271 20.9421 17.2589 21.167C13.7924 21.5544 10.209 21.5544 6.74244 21.167C4.73038 20.9421 3.10605 19.3571 2.8695 17.3346C2.45496 13.7903 2.45496 10.2097 2.8695 6.66543C3.10605 4.6429 4.73038 3.05789 6.74244 2.83301C9.37219 2.5391 12.0691 2.46815 14.7313 2.62016C14.9029 2.62996 14.9811 2.83757 14.8596 2.95909L13.867 3.95165C13.7883 4.03034 13.6804 4.07261 13.5692 4.06885C11.3428 3.99376 9.10122 4.07872 6.90905 4.32373C5.57894 4.47239 4.51346 5.522 4.35934 6.83968C3.95834 10.2682 3.95834 13.7318 4.35934 17.1603C4.51346 18.478 5.57894 19.5276 6.90905 19.6763C10.2649 20.0513 13.7365 20.0513 17.0923 19.6763C18.4224 19.5276 19.4879 18.478 19.642 17.1603Z" fill="#374957"/>
              </svg>
              <span class="btn-text">Edit</span>
            </button>
            <button class="icon-btn archive-btn" @click.stop="archiveNote">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M11 0V1H16V3H15V16C15 17.1 14.1 18 13 18H3C1.9 18 1 17.1 1 16V3H0V1H5V0H11ZM3 16H13V3H3V16ZM5 5H7V14H5V5ZM11 5H9V14H11V5Z" fill="#D10000"/>
              </svg>
              <span class="btn-text">Archive</span>
            </button>
          </div>
        </div>
        <div class="expanded-body">
          <!-- Edit Mode -->
          <transition name="edit-expand">
            <div v-if="isEditing" class="edit-form" @click.stop>
              <MentionTextarea
                v-model="editText"
                :customerNameMap="customerNameMap"
                :customerList="customerList"
              />
              <!-- Character counter removed - no limit -->
              <div class="edit-footer-row">
                <button class="update-btn" @click.stop="updateNote">Update</button>
              </div>
            </div>
          </transition>
          <!-- Normal View -->
          <p v-show="!isEditing" class="note-text" v-html="displayText"></p>
          <div class="expanded-footer">
            <button v-if="actualReplyCount > 0" class="action-btn view-btn" :class="{ 'active': showComments }" @click.stop="toggleComments">
              <!-- Hide icon when comments shown -->
              <svg v-if="showComments" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 5.9748C15.79 5.9748 19.17 8.1048 20.82 11.4748C20.23 12.6948 19.4 13.7448 18.41 14.5948L19.82 16.0048C21.21 14.7748 22.31 13.2348 23 11.4748C21.27 7.08481 17 3.9748 12 3.9748C10.73 3.9748 9.51 4.1748 8.36 4.5448L10.01 6.1948C10.66 6.0648 11.32 5.9748 12 5.9748ZM10.93 7.1148L13 9.1848C13.57 9.4348 14.03 9.8948 14.28 10.4648L16.35 12.5348C16.43 12.1948 16.49 11.8348 16.49 11.4648C16.5 8.9848 14.48 6.9748 12 6.9748C11.63 6.9748 11.28 7.0248 10.93 7.1148ZM2.01 3.8448L4.69 6.52481C3.06 7.8048 1.77 9.5048 1 11.4748C2.73 15.8648 7 18.9748 12 18.9748C13.52 18.9748 14.98 18.6848 16.32 18.1548L19.74 21.5748L21.15 20.1648L3.42 2.4248L2.01 3.8448ZM9.51 11.3448L12.12 13.9548C12.08 13.9648 12.04 13.9748 12 13.9748C10.62 13.9748 9.5 12.8548 9.5 11.4748C9.5 11.4248 9.51 11.3948 9.51 11.3448ZM6.11 7.9448L7.86 9.69481C7.63 10.2448 7.5 10.8448 7.5 11.4748C7.5 13.9548 9.52 15.9748 12 15.9748C12.63 15.9748 13.23 15.8448 13.77 15.6148L14.75 16.5948C13.87 16.8348 12.95 16.9748 12 16.9748C8.21 16.9748 4.83 14.8448 3.18 11.4748C3.88 10.0448 4.9 8.8648 6.11 7.9448Z" fill="#374957"/>
              </svg>
              <!-- Show icon when comments hidden -->
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 6.5C15.79 6.5 19.17 8.63 20.82 12C19.17 15.37 15.79 17.5 12 17.5C8.21 17.5 4.83 15.37 3.18 12C4.83 8.63 8.21 6.5 12 6.5ZM12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 9.5C13.38 9.5 14.5 10.62 14.5 12C14.5 13.38 13.38 14.5 12 14.5C10.62 14.5 9.5 13.38 9.5 12C9.5 10.62 10.62 9.5 12 9.5ZM12 7.5C9.52 7.5 7.5 9.52 7.5 12C7.5 14.48 9.52 16.5 12 16.5C14.48 16.5 16.5 14.48 16.5 12C16.5 9.52 14.48 7.5 12 7.5Z" fill="#374957"/>
              </svg>
              <span class="comments-text" :class="{ 'active': showComments }">{{ showComments ? 'Hide Comments' : commentsLabel }}</span>
            </button>
            <button class="action-btn reply-btn" :class="{ 'active': showReplyInput }" @click.stop="toggleReplyInput">
              <!-- Active gray icon -->
              <svg v-if="showReplyInput" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 20" fill="none">
                <path d="M8 4V0L3 5L8 10V6C11.31 6 14 8.69 14 12C14 15.31 11.31 18 8 18C4.69 18 2 15.31 2 12H0C0 16.42 3.58 20 8 20C12.42 20 16 16.42 16 12C16 7.58 12.42 4 8 4Z" fill="#E0E0E0"/>
              </svg>
              <!-- Default icon -->
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="18" viewBox="0 0 18 15" fill="none">
                <path d="M7 4V0L0 7L7 14V9.9C12 9.9 15.5 11.5 18 15C17 10 14 5 7 4Z" fill="#374957"/>
              </svg>
              <span class="reply-text" :class="{ 'gray': showReplyInput }">Reply</span>
            </button>
          </div>
          <!-- Reply Input Box - inside the blue area -->
          <transition name="reply-expand">
          <div v-if="showReplyInput" class="reply-input-wrapper" @click.stop>
            <MentionTextarea
              v-model="replyText"
              :customerNameMap="customerNameMap"
              :customerList="customerList"
              placeholder="Add a comment ..."
            />
            <!-- Character counter removed - no limit -->
            <div class="reply-footer-row">
              <button class="send-btn-inside" @click.stop="sendReply">Send</button>
            </div>
          </div>
          </transition>
        </div>
        <!-- Comments Section - separate blue areas -->
        <transition name="comments-expand">
        <div v-if="showComments" class="comments-section" @click.stop>
          <div 
            v-for="comment in comments" 
            :key="comment.id" 
            class="comment-card"
          >
            <div class="comment-header">
              <span class="comment-author">{{ comment.author }}</span>
              <span class="comment-time">{{ comment.time }}</span>
            </div>
            <p class="comment-text" v-html="comment.displayHtml"></p>
          </div>
        </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { notify } from '@/utils/notify'
import { updateInternalNoteApi, archiveInternalNoteApi, getRepliesApi, createReplyApi, markNoteAsReadApi } from '@/api/internalNotes'
import { mapState } from 'vuex'
import MentionTextarea from './MentionTextarea.vue'

export default {
  name: 'HistoryNoteCard',
  components: {
    MentionTextarea
  },
  emits: ['expand', 'archive', 'update'],
  data() {
    return {
      showReplyInput: false,
      replyText: '',
      showComments: false,
      isEditing: false,
      editText: '',
      isArchived: false,
      replies: [],
      isLoadingReplies: false
    };
  },
  props: {
    id: {
      type: String,
      required: true
    },
    patientId: {
      type: String,
      required: true
    },
    index: {
      type: Number,
      default: 0
    },
    totalCount: {
      type: Number,
      default: 0
    },
    author: {
      type: String,
      required: true
    },
    time: {
      type: String,
      required: true
    },
    text: {
      type: String,
      required: true
    },
    isExpanded: {
      type: Boolean,
      default: false
    },
    commentsCount: {
      type: Number,
      default: 0
    },
    replyCount: {
      type: Number,
      default: 0
    },
    customerNameMap: {
      type: Object,
      default: () => ({})
    },
    customerList: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    isExpanded(newVal) {
      if (newVal) {
        this.showReplyInput = false;
        this.replyText = '';
        this.showComments = false;
        // Fetch replies when card is expanded
        this.fetchReplies();
      }
    }
  },
  computed: {
    ...mapState('login', ['rc_user_id', 'userInfo']),

    isRightSide() {
      return this.totalCount > 0 && this.index >= this.totalCount - 3;
    },

    // Map backend replies to frontend format
    comments() {
      return this.replies.map(reply => this.mapReplyToFrontendFormat(reply));
    },

    // Get the actual reply count from the replies array (not the prop)
    actualReplyCount() {
      return this.replies.length;
    },

    // Comments label with proper pluralization
    commentsLabel() {
      return this.actualReplyCount === 1 ? '1 Comment' : `${this.actualReplyCount} Comments`;
    },

    // Transform note text for display with @Provider mentions
    displayText() {
      return this.transformMentionsToDisplay(this.text);
    },

    // Transform edit text for display with @Provider mentions
    displayEditText() {
      return this.transformMentionsToDisplay(this.editText);
    },

    // Get display text length for character counter
    displayEditTextLength() {
      return this.getDisplayTextLength(this.editText);
    },

    // Get display text length for reply character counter
    displayReplyTextLength() {
      return this.getDisplayTextLength(this.replyText);
    }
  },
  methods: {
    // Get display text length (replace backend format with provider names)
    getDisplayTextLength(backendText) {
      if (!backendText) return 0;

      // Replace @doctor_CUSTOMER_ID_CLINIC_ID with @ProviderName and count
      const mentionRegex = /@doctor_(\w+)_\w+/g;
      const displayText = backendText.replace(mentionRegex, (match, customerId) => {
        const providerName = this.customerNameMap[customerId];
        return providerName ? `@${providerName}` : match;
      });

      return displayText.length;
    },

    // Transform backend mention format to display format with HTML
    // Converts "@doctor_999997_clinicId" to "<span class="mention">@Provider</span>"
    transformMentionsToDisplay(text) {
      if (!text) return '';

      // Regex to match @doctor_CUSTOMER_ID_CLINIC_ID pattern
      const mentionRegex = /@doctor_(\w+)_\w+/g;

      // Replace all mentions with @Provider spans
      const transformed = text.replace(mentionRegex, (_match, customerId) => {
        // Get the provider name from customerNameMap
        const providerName = this.customerNameMap[customerId];
        if (providerName) {
          return `<span class="mention">@${providerName}</span>`;
        } else {
          // Fallback to just @Provider if name not found
          return `<span class="mention">@Provider</span>`;
        }
      });

      // Escape HTML special characters for non-mention text
      // We need to escape text that's not inside mention spans
      let result = '';
      let lastIndex = 0;
      const spanRegex = /<span class="mention">([^<]+)<\/span>/g;
      let match;

      while ((match = spanRegex.exec(transformed)) !== null) {
        // Add escaped text before the span
        const beforeText = transformed.substring(lastIndex, match.index);
        result += this.escapeHtml(beforeText);

        // Add the span (already safe)
        result += match[0];

        lastIndex = spanRegex.lastIndex;
      }

      // Add remaining text
      const remainingText = transformed.substring(lastIndex);
      result += this.escapeHtml(remainingText);

      return result;
    },

    // Escape HTML special characters
    escapeHtml(text) {
      if (!text) return '';
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    // Fetch replies from backend API
    async fetchReplies() {
      // Always fetch replies to get the latest data after sending a new reply
      this.isLoadingReplies = true;

      try {
        const response = await getRepliesApi(this.id, { include_nested: false });

        if (response && response.data) {
          this.replies = response.data;
        } else {
          this.replies = [];
        }
      } catch (error) {
        console.error('Error fetching replies:', error);
        this.replies = [];
      } finally {
        this.isLoadingReplies = false;
      }
    },

    // Map backend reply to frontend comment format
    mapReplyToFrontendFormat(reply) {
      // Format the created_at timestamp
      const formattedTime = this.formatReplyTimestamp(reply.created_at);

      // Get author ID from response (backend returns author_customer_id)
      const authorId = reply.author_customer_id || reply.author?.customer_id;

      // Display author name from customer name map, fallback to customer_id
      const authorName = this.customerNameMap[authorId] || authorId;

      // Transform mentions to display HTML
      const displayHtml = this.transformMentionsToDisplay(reply.content);

      return {
        id: reply.id,
        author: authorName,
        time: formattedTime,
        text: reply.content,
        displayHtml: displayHtml
      };
    },

    // Format reply timestamp
    formatReplyTimestamp(isoString) {
      if (!isoString) return '';

      const date = new Date(isoString);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const replyDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      const diffTime = today - replyDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      const timeStr = `${hours12}:${minutes} ${ampm}`;

      if (diffDays === 0) {
        return `Today ${timeStr}`;
      } else if (diffDays === 1) {
        return `Yesterday ${timeStr}`;
      } else {
        // Show date format instead of weekday for older notes
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month} ${day}, ${year} ${timeStr}`;
      }
    },

    handleCardClick() {
      // Only emit expand event if card is not already expanded
      // This allows clicking to expand, but not to collapse
      if (!this.isExpanded) {
        this.$emit('expand', this.id);
      }
    },

    // Mark note as read
    async markAsRead() {
      // Only mark as read if there are unread replies
      if (this.commentsCount === 0) {
        return;
      }

      try {
        await markNoteAsReadApi(this.id);

        // Emit update event to refresh parent (to update unread badge count)
        this.$emit('update');
      } catch (error) {
        console.error('Failed to mark note as read:', error);
        // Don't show notification for this, it's a background operation
      }
    },
    toggleReplyInput() {
      this.showReplyInput = !this.showReplyInput;
      if (!this.showReplyInput) {
        this.replyText = '';
      }
    },
    async sendReply() {
      if (!this.replyText.trim()) {
        return;
      }

      try {
        // Call API to create reply
        const data = {
          content: this.replyText
        };

        await createReplyApi(this.id, data);

        // Success notification
        notify({
          title: 'Success',
          message: 'Reply Sent Successfully',
          type: 'success',
          duration: 3000
        });

        // Clear input
        this.replyText = '';
        this.showReplyInput = false;

        // Refresh replies list
        await this.fetchReplies();

        // Emit update event to refresh parent (to update badge count)
        this.$emit('update');

      } catch (error) {
        console.error('Failed to send reply:', error);
        notify({
          title: 'Error',
          message: 'Failed to Send Reply',
          type: 'error',
          duration: 3000
        });
      }
    },
    async toggleComments() {
      this.showComments = !this.showComments;

      // Mark note as read when viewing comments
      if (this.showComments) {
        await this.markAsRead();
      }
    },
    startEdit() {
      if (this.isEditing) {
        this.isEditing = false;
        this.editText = '';
      } else {
        this.isEditing = true;
        this.editText = this.text;
      }
    },
    cancelEdit() {
      this.isEditing = false;
      this.editText = '';
    },
    clearEdit() {
      this.editText = '';
    },
    async updateNote() {
      if (!this.editText.trim()) {
        return;
      }

      try {
        // Call API to update note
        const data = {
          content: this.editText
        };

        await updateInternalNoteApi(this.patientId, this.id, data);

        // Success notification
        notify({
          title: 'Success',
          message: 'Note Updated Successfully',
          type: 'success',
          duration: 3000
        });

        // Exit edit mode
        this.isEditing = false;

        // Emit event to parent to refresh notes
        this.$emit('update');

      } catch (error) {
        console.error('Failed to update note:', error);
        notify({
          title: 'Error',
          message: 'Failed to Update Note',
          type: 'error',
          duration: 3000
        });
      }
    },
    async archiveNote() {
      try {
        // Call API to archive note
        await archiveInternalNoteApi(this.patientId, this.id);

        // Mark as archived locally (will be removed from list after refresh)
        this.isArchived = true;

        // Success notification
        notify({
          title: 'Success',
          message: 'Note Archived Successfully',
          type: 'success',
          duration: 3000
        });

        // Emit archive event to parent
        this.$emit('archive', this.id);

        // Emit update event to refresh notes list
        this.$emit('update');

      } catch (error) {
        console.error('Failed to archive note:', error);
        notify({
          title: 'Error',
          message: 'Failed to Archive Note',
          type: 'error',
          duration: 3000
        });
      }
    }
  }
};
</script>

<style scoped>
.history-note-card {
  background: white;
  border-radius: 5px;
  width: 183px;
  min-height: 48px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: width 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease;
  position: relative;
  z-index: 1;
  overflow: visible;
}

.history-note-card:first-child {
  margin-left: 8px;
}

.history-note-card:last-child {
  margin-right: 8px;
}

.history-note-card.expanded {
  width: 420px;
  z-index: 100 !important; 
  position: relative;
}

.comments-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 20px;
  height: 20px;
  padding: 2px 6px;
  border-radius: 12px;
  background: #1E4678;
  color: #FFF;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.2px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 200;
}

.compact-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
}

.expanded-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 12px 16px;
  border-radius: 5px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 100 !important;
  overflow: visible;
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
  transform-origin: top center;
  will-change: transform, opacity;
}

.history-note-card.is-right-side .expanded-content {
  left: auto;
  right: 0; 
}

.note-expand-enter-active {
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.note-expand-leave-active {
  transition: opacity 0.15s ease;
}

.note-expand-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

.note-expand-leave-to {
  opacity: 0;
}

.note-expand-enter-to,
.note-expand-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.expanded-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-right {
  display: flex;
  gap: 8px;
}

.icon-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  overflow: hidden;
}

.icon-btn .btn-text {
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  white-space: nowrap;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  transition: all 0.2s ease;
}

.icon-btn.edit-btn .btn-text {
  color: #374957;
}

.icon-btn.archive-btn .btn-text {
  color: #D10000;
}

.icon-btn:hover {
  background-color: #F3F4F6;
}

.icon-btn:hover .btn-text {
  max-width: 80px;
  opacity: 1;
  margin-left: 4px;
}

.expanded-body {
  background-color: #F8FBFD;
  padding: 12px;
  border-radius: 4px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.expanded-body::-webkit-scrollbar {
  width: 6px;
}

.expanded-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.expanded-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.expanded-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.expanded-footer {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.action-btn:hover {
  background-color: #E8EEF3;
}

.separator {
  color: #374957;
  font-size: 16px;
  font-weight: 600;
  margin: 0 4px;
}

.comments-text {
  color: #1E4678;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.1px;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.comments-text.active {
  color: #1E4670;
  font-family: "IBM Plex Sans Hebrew", Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  text-decoration: underline;
}

.reply-text {
  color: #374957;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.1px;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.reply-text.gray {
  color: #9E9E9E;
}

/* Reply expand animation */
.reply-expand-enter-active {
  animation: reply-slide-down 0.25s ease-out;
}

.reply-expand-leave-active {
  animation: reply-slide-up 0.2s ease-in;
}

/* Edit expand animation */
.edit-expand-enter-active {
  animation: reply-slide-down 0.25s ease-out;
}

.edit-expand-leave-active {
  animation: reply-slide-up 0.1s ease-in;
}

@keyframes reply-slide-down {
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    max-height: 250px;
    transform: translateY(0);
  }
}

@keyframes reply-slide-up {
  from {
    opacity: 1;
    max-height: 250px;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
}

.reply-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
  padding: 8px 12px;
  position: relative;
  border: 1px solid #C8C8C9;
  border-radius: 4px;
  background: #FFF;
  align-self: stretch;
  overflow: hidden;
}

/* MentionTextarea styles inside reply-input-wrapper */
.reply-input-wrapper :deep(.mention-editor) {
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.25px;
  color: #333;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  resize: none;
  min-height: 28px !important;
  max-height: 140px !important;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Scrollbar for reply input */
.reply-input-wrapper :deep(.mention-editor::-webkit-scrollbar) {
  width: 8px;
}

.reply-input-wrapper :deep(.mention-editor::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 4px;
}

.reply-input-wrapper :deep(.mention-editor::-webkit-scrollbar-thumb:hover) {
  background: #a8a8a8;
}

.reply-input-wrapper :deep(.mention-editor::placeholder) {
  color: #999;
}

/* Legacy el-input styles (kept for compatibility) */
.reply-input-wrapper :deep(.el-textarea__inner) {
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.25px;
  color: #333;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  resize: none;
  min-height: 28px !important;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Scrollbar for legacy el-textarea */
.reply-input-wrapper :deep(.el-textarea__inner::-webkit-scrollbar) {
  width: 8px;
}

.reply-input-wrapper :deep(.el-textarea__inner::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 4px;
}

.reply-input-wrapper :deep(.el-textarea__inner::-webkit-scrollbar-thumb:hover) {
  background: #a8a8a8;
}

.reply-input-wrapper :deep(.el-textarea) {
  width: 100%;
}

.reply-input-wrapper :deep(.el-textarea__inner::placeholder) {
  color: #999;
}

.reply-footer-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

/* Character counter inside reply input */
.reply-character-counter-inside {
  position: absolute;
  bottom: 6px;
  left: 8px;
  color: #999;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  pointer-events: none;
}

.reply-character-counter {
  margin-top: 6px;
  color: #999;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.send-btn-inside {
  background: none;
  border: none;
  cursor: pointer;
  color: #1E4678;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 28px;
  padding: 0;
  text-decoration: underline;
}

.time-text {
  color: #667085;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.author-text {
  color: #333333;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
}

.note-text {
  font-size: 14px;
  line-height: 1.4;
  color: #1F2937;
  margin: 0;
  /* Limit to approximately 5 lines */
  max-height: 7em; /* 1.4 * 5 = 7 */
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #F9FBFD;
}

/* Style for mentions in note text */
.note-text :deep(.mention) {
  color: #25B8C2;
  font-family: Inter, system-ui, -apple-system, sans-serif;
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  line-height: 1.4;
  white-space: nowrap;
}

/* Comments Section */
.comments-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
  overflow-y: auto;
  max-height: 300px;
}

.comments-section::-webkit-scrollbar {
  width: 6px;
}

.comments-section::-webkit-scrollbar-track {
  background: #F9FBFD;
  border-radius: 3px;
}

.comments-section::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.comments-section::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.comment-card {
  padding: 12px;
  background: #F8FBFD;
  border-radius: 4px;
}

.comment-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.comment-author {
  color: #333;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.comment-time {
  color: #999;
  font-family: "IBM Plex Sans Hebrew", Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.4px;
}

.comment-text {
  color: #333;
  font-family: "IBM Plex Sans Hebrew", Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  margin: 0;
}

/* Style for mentions in comment text */
.comment-text :deep(.mention) {
  color: #25B8C2;
  font-family: Inter, system-ui, -apple-system, sans-serif;
  font-size: 14px;
  font-weight: 500;
  font-style: normal;
  line-height: normal;
  white-space: nowrap;
}

.mention {
  color: #25B8C2;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

/* Comments expand animation */
.comments-expand-enter-active {
  animation: reply-slide-down 0.25s ease-out;
}

.comments-expand-leave-active {
  animation: reply-slide-up 0.2s ease-in;
}

/* Edit Form */
.edit-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid #C8C8C9;
  border-radius: 4px;
  background: #FFF;
  position: relative;
}

/* MentionTextarea styles inside edit-form */
.edit-form :deep(.mention-editor) {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #333;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  resize: none;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #FFF;
}

.edit-form :deep(.mention-editor::-webkit-scrollbar) {
  width: 6px;
}

.edit-form :deep(.mention-editor::-webkit-scrollbar-track) {
  background: #FFF;
  border-radius: 3px;
}

.edit-form :deep(.mention-editor::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 3px;
}

.edit-form :deep(.mention-editor::-webkit-scrollbar-thumb:hover) {
  background: #a8a8a8;
}

.edit-form :deep(.mention-editor::placeholder) {
  color: #999;
}

/* Legacy el-input styles (kept for compatibility) */
.edit-form :deep(.el-textarea__inner) {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #333;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  resize: none;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #FFF;
}

.edit-form :deep(.el-textarea__inner::-webkit-scrollbar) {
  width: 6px;
}

.edit-form :deep(.el-textarea__inner::-webkit-scrollbar-track) {
  background: #FFF;
  border-radius: 3px;
}

.edit-form :deep(.el-textarea__inner::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 3px;
}

.edit-form :deep(.el-textarea__inner::-webkit-scrollbar-thumb:hover) {
  background: #a8a8a8;
}

.edit-form :deep(.el-textarea__inner::placeholder) {
  color: #999;
}

.edit-footer-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

/* Character counter inside edit form */
.edit-character-counter-inside {
  position: absolute;
  bottom: 6px;
  left: 8px;
  color: #999;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  pointer-events: none;
}

.edit-character-counter {
  margin-top: 6px;
  color: #999;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.update-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #1E4678;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  padding: 0;
  text-decoration: underline;
}
</style>
